// Mirror of backend/src/models/tracking.ts currentSowValue and
// totalContractValue. Update both together; the test suites exercise
// identical scenarios on each side so a backend formula change without
// a frontend update breaks the frontend test suite, not just runtime.
//
// Multi-target variations: a variation may apply to the contract AND/OR
// multiple SOWs simultaneously. Per-SOW current value uses the
// three-case rule:
//
//   1. If every target on the variation is allocated, the SOW's
//      contribution is the sum of allocations on the variation's targets
//      that point at this SOW (handles the rare same-sowId-twice case).
//   2. If no target is allocated AND the variation has exactly one
//      target — and that target is this SOW — the SOW's contribution is
//      the full `valueImpact`. This is the legacy single-sow shape.
//   3. If no target is allocated AND the variation is multi-target, the
//      SOW's contribution is 0. Per-SOW attribution requires an explicit
//      partition in multi-target mode; without it the value is "with the
//      variation" but not attributable to any single SOW.
//
// `totalContractValue` is unchanged by allocations: a variation
// contributes its full valueImpact to the contract total exactly once,
// regardless of how it's partitioned.

import type { Sow, Variation, VariationTarget } from "../../../../types";
import { readTargets } from "./variationTargets";

/** Minimal shape — accepts either a frontend Sow or any compatible
 *  object so the helper does not couple to the discriminated union. */
export interface SowLike {
  id: string;
  originalValue: number;
  status: Sow["status"];
  archivedAt?: string;
}

/** Minimal shape — same contract as the backend `VariationLike`.
 *  `targets` is the canonical multi-target shape. The legacy
 *  `variesType` + `variesId` fields are accepted as a read fallback for
 *  records persisted before the multi-target migration. */
export interface VariationLike {
  id: string;
  targets?: VariationTarget[];
  variesType?: Variation["variesType"];
  variesId?: Variation["variesId"];
  valueImpact: number | null;
  status: Variation["status"];
  archivedAt?: string;
}

function readVariationTargets(v: VariationLike): VariationTarget[] {
  if (v.targets && v.targets.length > 0) return v.targets;
  if (v.variesType === "sow" && v.variesId) {
    return [{ kind: "sow", sowId: v.variesId }];
  }
  return [{ kind: "contract" }];
}

function isAllocated(t: VariationTarget): boolean {
  return t.allocation !== null && t.allocation !== undefined;
}

/** Classification of a variation's contribution to a single SOW under
 *  the multi-target three-case rule.
 *
 *  - `none`: this SOW is not a target of the variation.
 *  - `allocated`: every target on the variation has an explicit
 *    allocation; the SOW's contribution is the sum of allocations on
 *    targets that point at this SOW.
 *  - `sole-target`: the variation has exactly one target which is this
 *    SOW (legacy single-sow shape); contribution is the full
 *    `valueImpact`.
 *  - `unallocated-multi`: the variation targets multiple things but no
 *    allocations are set. Per-SOW attribution is undefined; the SOW's
 *    contribution is 0 for arithmetic purposes, but the full
 *    `valueImpact` is returned alongside as `totalImpact` so renderers
 *    can surface "(unallocated, total $X)" informationally. */
export type SowVariationMode =
  | "none"
  | "allocated"
  | "sole-target"
  | "unallocated-multi";

export interface SowVariationClassification {
  mode: SowVariationMode;
  /** Contribution this variation makes to this SOW's current value. 0
   *  for `none` and `unallocated-multi`. */
  contribution: number;
  /** Full `valueImpact` of the variation (null for non-financial). Same
   *  for every mode; surfaced so renderers can show the unallocated
   *  total without re-reading from the variation. */
  totalImpact: number | null;
  /** Number of targets on the variation. Used by renderers to label the
   *  "across N targets" caption in unallocated-multi mode. */
  targetCount: number;
}

/** Classify a variation's contribution to a single SOW under the
 *  three-case rule. Pure — does not filter on status / archived /
 *  non-financial; callers decide whether the classification is
 *  meaningful (currentSowValue ignores all three; the SOW detail
 *  variation list shows proposed + approved alike). */
export function classifySowVariation(
  sowId: string,
  v: VariationLike,
): SowVariationClassification {
  const targets = readVariationTargets(v);
  const sowTargets = targets.filter(
    (t) => t.kind === "sow" && t.sowId === sowId,
  );
  const totalImpact = v.valueImpact ?? null;
  const targetCount = targets.length;
  if (sowTargets.length === 0) {
    return { mode: "none", contribution: 0, totalImpact, targetCount };
  }
  const everyAllocated = targets.every(isAllocated);
  if (everyAllocated) {
    const contribution = sowTargets.reduce(
      (sum, t) => sum + (t.allocation ?? 0),
      0,
    );
    return { mode: "allocated", contribution, totalImpact, targetCount };
  }
  if (targets.length === 1 && sowTargets.length === 1) {
    return {
      mode: "sole-target",
      contribution: v.valueImpact ?? 0,
      totalImpact,
      targetCount,
    };
  }
  return {
    mode: "unallocated-multi",
    contribution: 0,
    totalImpact,
    targetCount,
  };
}

/** Per-SOW contribution from a single variation, applying the three-case
 *  rule. Returns 0 when the variation does not target this SOW. Thin
 *  wrapper around `classifySowVariation` so the arithmetic path stays
 *  unchanged for callers that only need the number. */
function sowContribution(sowId: string, v: VariationLike): number {
  return classifySowVariation(sowId, v).contribution;
}

/**
 * Derive a SOW's current value: original value plus the sum of approved,
 * non-archived variation contributions. Contributions follow the
 * multi-target three-case rule documented at the top of this module.
 * Non-financial (null) variations contribute zero.
 */
export function currentSowValue(
  sow: { id: string; originalValue: number },
  variations: VariationLike[],
): number {
  return variations
    .filter(
      (v) =>
        v.status === "approved" && !v.archivedAt && v.valueImpact !== null,
    )
    .reduce(
      (sum, v) => sum + sowContribution(sow.id, v),
      sow.originalValue,
    );
}

/** Approved variations affecting this SOW, sorted by approvedAt fallback to
 *  effectiveDate if present, else stable. Used by the SOW expanded view's
 *  current-value annotation: `$120,000 + $20,000 from VAR-2026-002`. */
export function approvedSowVariations(
  sowId: string,
  variations: Variation[],
): Variation[] {
  return variations
    .filter((v) => {
      if (v.status !== "approved") return false;
      if (v.archivedAt) return false;
      if (v.valueImpact === null) return false;
      return readTargets(v).some(
        (t) => t.kind === "sow" && t.sowId === sowId,
      );
    })
    .sort((a, b) => {
      const ka = a.approvedAt ?? a.effectiveDate ?? "";
      const kb = b.approvedAt ?? b.effectiveDate ?? "";
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });
}

/** All non-archived non-rejected variations affecting this SOW, sorted
 *  by `effectiveDate` ascending. Used by the SOW detail view's
 *  "Variations affecting this SOW" section so reviewers can see what's
 *  in flight (proposed) alongside what's already settled (approved).
 *  Rejected variations are excluded — they are settled and
 *  non-informational. Non-financial variations ARE included so reviewers
 *  see contractual scope changes that don't move the dollar value. */
export function sowVariations(
  sowId: string,
  variations: Variation[],
): Variation[] {
  return variations
    .filter((v) => {
      if (v.archivedAt) return false;
      if (v.status === "rejected") return false;
      return readTargets(v).some(
        (t) => t.kind === "sow" && t.sowId === sowId,
      );
    })
    .sort((a, b) => {
      const ka = a.effectiveDate ?? "";
      const kb = b.effectiveDate ?? "";
      return ka < kb ? -1 : ka > kb ? 1 : 0;
    });
}

/** Per-contract contribution from a single variation. The contract is
 *  treated symmetrically with a SOW under the multi-target three-case
 *  rule: allocated targets contribute their summed allocations on
 *  `kind: "contract"` entries; a sole `kind: "contract"` target
 *  contributes the full `valueImpact`; an unallocated multi-target
 *  variation contributes 0 (per-target attribution is undefined without
 *  an explicit partition).
 *
 *  Used by `contractOnlyValue` to derive the scope-restricted total
 *  shown on surfaces filtered to "contract scope" (e.g. the milestone
 *  tab's value tile when the user has selected the head contract). */
function contractContribution(v: VariationLike): number {
  const targets = readVariationTargets(v);
  const contractTargets = targets.filter((t) => t.kind === "contract");
  if (contractTargets.length === 0) return 0;
  const everyAllocated = targets.every(isAllocated);
  if (everyAllocated) {
    return contractTargets.reduce((sum, t) => sum + (t.allocation ?? 0), 0);
  }
  if (targets.length === 1 && contractTargets.length === 1) {
    return v.valueImpact ?? 0;
  }
  return 0;
}

/**
 * Derive the contract-scope value: head value plus the sum of approved,
 * non-archived variation contributions that target the head contract,
 * applying the same multi-target three-case rule used by `currentSowValue`.
 * SOWs do NOT contribute. Used when a user filters a surface to
 * "contract scope" (the head contract slice, excluding SOWs).
 * Non-financial (null) variations contribute zero.
 */
export function contractOnlyValue(
  contract: { value: number },
  variations: VariationLike[],
): number {
  return variations
    .filter(
      (v) =>
        v.status === "approved" && !v.archivedAt && v.valueImpact !== null,
    )
    .reduce((sum, v) => sum + contractContribution(v), contract.value);
}

/**
 * Derive a contract's total value by summing the head value, the original
 * value of every non-archived non-cancelled SOW, and the value impact of
 * every approved non-archived variation (null impact = 0). Returns both the
 * total and the breakdown so callers can render a stacked-bar visualisation
 * without recomputing the parts.
 */
export function totalContractValue(
  contract: { value: number },
  sows: SowLike[],
  variations: VariationLike[],
): {
  total: number;
  breakdown: { head: number; sows: number; variations: number };
} {
  const head = contract.value;
  // Only Active and Completed SOWs contribute to the contract's
  // committed value. Draft and Cancelled SOWs are excluded — Draft has
  // not been agreed yet and Cancelled was rejected after creation.
  // Mirrors `totalContractValue` on the backend so the two surfaces
  // never drift.
  const sowsValue = sows
    .filter(
      (s) =>
        !s.archivedAt && (s.status === "active" || s.status === "completed"),
    )
    .reduce((sum, s) => sum + s.originalValue, 0);
  const variationsValue = variations
    .filter(
      (v) =>
        !v.archivedAt && v.status === "approved" && v.valueImpact !== null,
    )
    .reduce((sum, v) => sum + (v.valueImpact ?? 0), 0);
  return {
    total: head + sowsValue + variationsValue,
    breakdown: { head, sows: sowsValue, variations: variationsValue },
  };
}
